import React from 'react';
import { graphql } from 'gatsby';
import styled, { css } from 'styled-components';
import ReactMarkdown from 'react-markdown/with-html';
import format from 'date-fns/format';
import Img from 'gatsby-image/withIEPolyfill';
import PropTypes from 'prop-types';

import Layout from '../components/Layout';
import { T5D, T4R, T1M } from '../components/Text';
import Description from '../components/Description';
import Share from '../components/Share';
import SEO from '../components/Seo';
import PostCardContainer from '../components/Containers/PostCardContainer';
import Container from '../components/Containers/Container';
import Card from '../components/Card';

import {
  linkRenderer,
  parsedHtmlRenderer,
  imageRenderer
} from '../helpers/renderers';
import { getURL } from '../helpers/url';

import { colors, media } from '../styles/constants';
import { small } from '../styles/text';
import { link } from '../styles/link';
import postStyle from '../styles/post';

const PostHeader = styled.header`
  max-width: 97rem;
  color: ${colors.white};
  margin-bottom: 5rem;

  img {
    width: 100%;
  }

  ${T5D} {
    margin-bottom: 0.5rem;
  }

  ${T4R} {
    margin-bottom: 2.5rem;

    @media (min-width: ${media.tabletPortrait}) {
      margin-bottom: 4rem;
    }
  }
`;

const DateAndHandleContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 3rem;
  margin-bottom: -1rem;
  color: ${colors.graphite};

  span:first-child {
    margin-bottom: 0.5rem;
  }

  a {
    ${small}
    ${link}
    color: ${colors.graphite};
  }

  @media (min-width: ${media.tabletPortrait}) {
    margin-top: 4rem;
    margin-bottom: 0rem;
  }
`;

const PostContainer = styled.article`
  ${postStyle}

  img {
    width: 100%;
    max-width: none;
    margin-bottom: 4rem;
  }
`;

const PostFooter = styled.div`
  max-width: 97rem;

  ${props =>
    props.addExtraMargin
      ? css`
          ${Container} {
            margin-bottom: 6rem;
            margin-top: 6rem;

            @media (min-width: ${media.tabletPortrait}) {
              margin-bottom: 16rem;
              margin-top: 9rem;
            }

            @media (min-width: ${media.desktop}) {
              margin-bottom: 20rem;
            }
          }
        `
      : css`
          ${Container} {
            margin-bottom: 3rem;
            margin-top: 6rem;

            @media (min-width: ${media.tabletPortrait}) {
              margin-bottom: 8rem;
              margin-top: 9rem;
            }

            @media (min-width: ${media.desktop}) {
              margin-bottom: 10rem;
            }
          }
        `}
`;

const PostTemplate = ({
  location: { pathname },
  data: {
    strapiPost: post,
    strapiAuthor: author,
    allFile: optimizedImages,
    allStrapiPost: related
  }
}) => {
  return (
    <>
      <SEO
        title={post.title}
        description={
          post.thumbnaildescription || post.subtitle || post.shortsubtitle
        }
        image={
          post.thumbnail.childImageSharp
            ? post.thumbnail.childImageSharp.fluid.src
            : post.thumbnail.publicURL
        }
        imageAlt={post.imagealt}
        pathname={pathname}
        postData={{
          author: author.name,
          date: post.date
        }}
      />
      <Layout>
        <PostHeader>
          <T5D>{post.title}</T5D>
          {post.subtitle && <T4R as="h2">{post.subtitle}</T4R>}
          {post.image.childImageSharp ? (
            <Img
              fluid={post.image.childImageSharp.fluid}
              alt={post.imagealt}
              loading="eager"
            />
          ) : (
            <img src={post.image.publicURL} alt={post.imagealt} />
          )}
          <DateAndHandleContainer>
            <T1M>{format(post.date, 'MMMM D, YYYY')}</T1M>
            <T1M>
              {author.name}
              {author.twitterhandle && (
                <a
                  href={`https://twitter.com/${author.twitterhandle}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {` | @${author.twitterhandle}`}
                </a>
              )}
            </T1M>
          </DateAndHandleContainer>
        </PostHeader>
        <PostContainer>
          <ReactMarkdown
            source={post.content}
            renderers={{
              link: linkRenderer,
              parsedHtml: args =>
                parsedHtmlRenderer(args, optimizedImages.edges),
              image: args => imageRenderer(args, optimizedImages.edges)
            }}
            escapeHtml={false}
          />
          <hr />
        </PostContainer>
        <PostFooter addExtraMargin={related.edges.length === 0}>
          <Container>
            <Share
              pathname={pathname}
              title={post.title}
              summary={
                post.thumbnaildescription || post.subtitle || post.shortsubtitle
              }
            />
            <Description
              imgSrc={author.picture}
              imgAlt={`The post's author ${author.name}`}
              title={author.name}
              text={author.bio}
            />
          </Container>
          {related.edges.length > 0 && (
            <Container>
              <T5D as="h2">Related Thoughts</T5D>
              <PostCardContainer>
                {related.edges.map(related => (
                  <Card
                    key={related.node.id}
                    imgSrc={related.node.image}
                    imgAlt={related.node.title}
                    title={related.node.title}
                    text={related.node.shortsubtitle}
                    tags={related.node.tags}
                    link={getURL('observatory', related.node.title)}
                  />
                ))}
              </PostCardContainer>
            </Container>
          )}
        </PostFooter>
      </Layout>
    </>
  );
};

PostTemplate.propTypes = {
  data: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired
};

export default PostTemplate;

export const query = graphql`
  query PostTemplate($id: String!, $authorId: String!, $related: [String!]) {
    strapiPost(id: { eq: $id }) {
      title
      subtitle
      shortsubtitle
      content
      date
      image {
        publicURL
        childImageSharp {
          fluid(
            maxWidth: 4096
            maxHeight: 2160
            cropFocus: CENTER
            quality: 75
          ) {
            ...GatsbyImageSharpFluid_withWebp_noBase64
          }
        }
      }
      imagealt
      thumbnail {
        publicURL
        childImageSharp {
          fluid(maxWidth: 520, cropFocus: CENTER) {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
      thumbnaildescription
    }
    strapiAuthor(id: { eq: $authorId }) {
      name
      twitterhandle
      bio
      picture {
        publicURL
        childImageSharp {
          fixed(width: 120, height: 120, cropFocus: CENTER) {
            ...GatsbyImageSharpFixed_noBase64
          }
        }
      }
    }
    allFile(filter: { parent: { id: { eq: $id } } }) {
      edges {
        node {
          url
          publicURL
          childImageSharp {
            fluid(maxWidth: 970) {
              ...GatsbyImageSharpFluid_noBase64
              presentationWidth
            }
          }
        }
      }
    }
    allStrapiPost(filter: { id: { in: $related } }) {
      edges {
        node {
          id
          title
          shortsubtitle
          tags {
            name
          }
          image {
            publicURL
            childImageSharp {
              fluid(maxWidth: 700, maxHeight: 440, cropFocus: CENTER) {
                ...GatsbyImageSharpFluid_noBase64
              }
            }
          }
        }
      }
    }
  }
`;
